import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import Search from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Edit from "@material-ui/icons/Edit";
import Close from "@material-ui/icons/Close";
import Tooltip from "@material-ui/core/Tooltip";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AddShoppingCart from "@material-ui/icons/AddShoppingCart";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);

const useDrawerStyles = makeStyles({
  paper: { width: 360, padding: "0" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #e0e0e0",
  },
  title: { fontSize: "16px", fontWeight: 700, color: "#222", margin: 0 },
  body: {
    padding: "24px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  footer: {
    padding: "16px 20px",
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
  },
});

const useCustomerDrawerStyles = makeStyles({
  paper: { width: 480, display: "flex", flexDirection: "column" },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "16px 20px 12px",
  },
  body: { flex: 1, overflowY: "auto", padding: "0 20px" },
  footer: { padding: "12px 20px", borderTop: "1px solid #e0e0e0" },
});

const CUSTOMER_INVOICES_QUERY = `
  query GetCustomerInvoices($customerId: String!, $page: Int!, $pageSize: Int!) {
    invoicesWithPagination(filter: { customerId: $customerId, page: $page, pageSize: $pageSize, isReturn: false }) {
      data { id invoiceNumber invoiceDate status total paid }
      total
    }
  }
`;

const useCustomStyles = makeStyles({
  filterRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "4px 10px",
    backgroundColor: "#fff",
    height: "34px",
  },
  selectBox: {
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "14px",
    backgroundColor: "#fff",
    height: "34px",
  },
  dateInput: {
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "5px 10px",
    fontSize: "14px",
    outline: "none",
    color: "#333",
    height: "34px",
    boxSizing: "border-box",
  },
  filterLabel: {
    fontSize: "13px",
    color: "#555",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  activeFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "8px",
  },
  invoiceNum: {
    fontWeight: 600,
    color: "#1565c0",
    fontSize: "13px",
  },
});

const GRAPHQL_URL =
  (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const UPDATE_STATUS_MUTATION = `
  mutation UpdateStatus($id: String!, $status: String!) {
    updateInvoiceStatus(id: $id, status: $status) { id status }
  }
`;

// Allowed next statuses per current status
const TRANSITIONS = {
  Processing: ["Paid", "Cancelled"],
  Paid: ["Shipped", "Completed", "Cancelled"],
  Shipped: ["Completed", "Returned"],
  Completed: ["Returned"],
};

const QUERY = `
  query GetInvoices($filter: InvoiceFilterDto!) {
    invoicesWithPagination(filter: $filter) {
      data {
        id
        invoiceNumber
        invoiceDate
        returnNumber
        status
        subtotal
        discount
        paid
        customer { id customerNumber name }
      }
      total page pageSize totalPages
    }
  }
`;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Draft", value: "Draft" },
  { label: "Processing", value: "Processing" },
  { label: "Paid", value: "Paid" },
  { label: "Shipped", value: "Shipped" },
  { label: "Completed", value: "Completed" },
  { label: "Returned", value: "Returned" },
  { label: "Cancelled", value: "Cancelled" },
];

const INVOICE_TYPE_OPTIONS = [
  { label: "All types", value: "" },
  { label: "Not delivery", value: "not_delivery" },
  { label: "Delivery", value: "delivery" },
];

const PAYMENT_METHOD_OPTIONS = [
  { label: "Payment method", value: "" },
  { label: "Cash", value: "cash" },
  { label: "Card", value: "card" },
  { label: "Transfer", value: "transfer" },
  { label: "Wallet", value: "wallet" },
];

const TIME_PRESETS = [
  { label: "All time", value: "all" },
  { label: "Today", value: "today" },
  { label: "This week", value: "week" },
  { label: "This month", value: "month" },
  { label: "Custom range", value: "custom" },
];

function getPresetDates(preset) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (preset === "today") {
    const s = fmt(now);
    return { dateFrom: s, dateTo: s };
  }
  if (preset === "week") {
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { dateFrom: fmt(mon), dateTo: fmt(sun) };
  }
  if (preset === "month") {
    return {
      dateFrom: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      dateTo: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  return { dateFrom: "", dateTo: "" };
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatNum(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

const STATUS_COLORS = {
  Processing: { bg: "#fff3e0", color: "#e65100" },
  Paid: { bg: "#e8eaf6", color: "#3949ab" },
  Shipped: { bg: "#e0f7fa", color: "#00838f" },
  Completed: { bg: "#e8f5e9", color: "#2e7d32" },
  Returned: { bg: "#fce4ec", color: "#ad1457" },
  Cancelled: { bg: "#f5f5f5", color: "#757575" },
  "Shipment returned": { bg: "#e3f2fd", color: "#0d47a1" },
};

function StatusBadge({ status }) {
  if (!status) return null;
  const c = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#555" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.color,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

function ClickableStatus({ status, onClick }) {
  const c = STATUS_COLORS[status] || { bg: "#f5f5f5", color: "#555" };
  const hasTransition = !!(
    TRANSITIONS[status] && TRANSITIONS[status].length > 0
  );
  return (
    <span
      onClick={hasTransition ? onClick : undefined}
      title={hasTransition ? "Click to update status" : undefined}
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.color,
        whiteSpace: "nowrap",
        cursor: hasTransition ? "pointer" : "default",
        textDecoration: hasTransition ? "underline dotted" : "none",
        textUnderlineOffset: "2px",
      }}
    >
      {status}
    </span>
  );
}

const EMPTY_FILTER = {
  invoiceNumber: "",
  customerNumber: "",
  customerName: "",
  status: "",
  invoiceType: "",
  paymentMethod: "",
  dateFrom: "",
  dateTo: "",
};

export default function InvoicesPage() {
  const classes = useStyles();
  const custom = useCustomStyles();
  const drawer = useDrawerStyles();
  const custDrawer = useCustomerDrawerStyles();
  const history = useHistory();

  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");

  // Text search states
  const [invoiceNumber, setInvoiceNumber] = React.useState("");
  const [customerNumber, setCustomerNumber] = React.useState("");
  const [customerName, setCustomerName] = React.useState("");

  // Dropdown filter states
  const [status, setStatus] = React.useState("");
  const [invoiceType, setInvoiceType] = React.useState("");
  const [paymentMethod, setPaymentMethod] = React.useState("");
  const [timePeriod, setTimePeriod] = React.useState("all");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  const filterRef = React.useRef({ ...EMPTY_FILTER });
  const debounceRef = React.useRef(null);

  const fetchInvoices = React.useCallback((pg, ps, f) => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: QUERY,
        variables: {
          filter: {
            page: pg,
            pageSize: ps,
            isReturn: false,
            invoiceNumber: f.invoiceNumber || undefined,
            customerNumber: f.customerNumber || undefined,
            customerName: f.customerName || undefined,
            status: f.status || undefined,
            invoiceType: f.invoiceType || undefined,
            paymentMethod: f.paymentMethod || undefined,
            dateFrom: f.dateFrom || undefined,
            dateTo: f.dateTo || undefined,
          },
        },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
          return;
        }
        const p = data.invoicesWithPagination;
        setInvoices(p.data);
        setTotal(p.total);
        setTotalPages(p.totalPages);
        setPage(p.page);
        setPageInput(String(p.page));
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    filterRef.current = { ...EMPTY_FILTER };
    fetchInvoices(1, pageSize, filterRef.current);
  }, []);

  // Debounced text search helper
  const debounce = (updates) => {
    filterRef.current = { ...filterRef.current, ...updates };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchInvoices(1, pageSize, filterRef.current);
    }, 400);
  };

  // Immediate dropdown change helper
  const immediate = (updates) => {
    const next = { ...filterRef.current, ...updates };
    filterRef.current = next;
    fetchInvoices(1, pageSize, next);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    immediate({ status: e.target.value });
  };
  const handleTypeChange = (e) => {
    setInvoiceType(e.target.value);
    immediate({ invoiceType: e.target.value });
  };
  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
    immediate({ paymentMethod: e.target.value });
  };

  const handleTimePeriodChange = (e) => {
    const val = e.target.value;
    setTimePeriod(val);
    if (val !== "custom") {
      const { dateFrom: df, dateTo: dt } = getPresetDates(val);
      setDateFrom(df);
      setDateTo(dt);
      immediate({ dateFrom: df, dateTo: dt });
    }
  };
  const handleDateFromChange = (e) => {
    setDateFrom(e.target.value);
    immediate({ dateFrom: e.target.value });
  };
  const handleDateToChange = (e) => {
    setDateTo(e.target.value);
    immediate({ dateTo: e.target.value });
  };

  const clearFilter = (key) => {
    const updates = {};
    if (key === "status") {
      updates.status = "";
      setStatus("");
    } else if (key === "invoiceType") {
      updates.invoiceType = "";
      setInvoiceType("");
    } else if (key === "paymentMethod") {
      updates.paymentMethod = "";
      setPaymentMethod("");
    } else if (key === "date") {
      updates.dateFrom = "";
      updates.dateTo = "";
      setDateFrom("");
      setDateTo("");
      setTimePeriod("all");
    }
    immediate(updates);
  };

  // Customer invoice drawer state
  const [invDrawer, setInvDrawer] = React.useState({
    open: false,
    customer: null,
  });
  const [custInvoices, setCustInvoices] = React.useState([]);
  const [custInvTotal, setCustInvTotal] = React.useState(0);
  const [custInvLoading, setCustInvLoading] = React.useState(false);

  const openInvDrawer = (customer) => {
    setInvDrawer({ open: true, customer });
    setCustInvoices([]);
    setCustInvLoading(true);
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: CUSTOMER_INVOICES_QUERY,
        variables: { customerId: customer.id, page: 1, pageSize: 50 },
      }),
    })
      .then((r) => r.json())
      .then(({ data }) => {
        setCustInvoices(data?.invoicesWithPagination?.data || []);
        setCustInvTotal(data?.invoicesWithPagination?.total || 0);
      })
      .finally(() => setCustInvLoading(false));
  };

  const [drawerInv, setDrawerInv] = React.useState(null);
  const [drawerStatus, setDrawerStatus] = React.useState("");
  const [drawerSaving, setDrawerSaving] = React.useState(false);

  const openDrawer = (inv) => {
    setDrawerInv(inv);
    setDrawerStatus("");
  };
  const closeDrawer = () => {
    setDrawerInv(null);
    setDrawerStatus("");
  };

  const handleSaveStatus = () => {
    if (!drawerStatus || !drawerInv) return;

    // Returned: skip status update, go straight to return creation
    if (drawerStatus === "Returned") {
      const invId = drawerInv.id;
      closeDrawer();
      history.push(`/admin/invoices/${invId}/return`);
      return;
    }

    setDrawerSaving(true);
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: UPDATE_STATUS_MUTATION,
        variables: { id: drawerInv.id, status: drawerStatus },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          alert(errors[0].message);
          return;
        }
        const saved = data.updateInvoiceStatus.status;
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === drawerInv.id ? { ...inv, status: saved } : inv
          )
        );
        closeDrawer();
      })
      .catch(() => alert("Cannot connect to server"))
      .finally(() => setDrawerSaving(false));
  };

  const handlePageSizeChange = (e) => {
    const ps = e.target.value;
    setPageSize(ps);
    fetchInvoices(1, ps, filterRef.current);
  };
  const goToPage = (pg) => {
    if (pg < 1 || pg > totalPages) return;
    fetchInvoices(pg, pageSize, filterRef.current);
  };
  const handlePageInputKey = (e) => {
    if (e.key === "Enter") {
      const pg = parseInt(pageInput, 10);
      if (!isNaN(pg)) goToPage(pg);
    }
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = invoices.map((inv) => [
    <div
      key={inv.id + "-actions"}
      style={{ display: "flex", gap: "2px", alignItems: "center" }}
    >
      <Tooltip title="Edit delivery" placement="top">
        <IconButton
          size="small"
          onClick={() => history.push(`/admin/invoices/${inv.id}/edit`)}
        >
          <Edit
            fontSize="small"
            style={{
              color: ["Draft", "Processing", "Paid", "Shipped"].includes(
                inv.status
              )
                ? "#1976d2"
                : "#bdbdbd",
            }}
          />
        </IconButton>
      </Tooltip>
    </div>,
    <div key={inv.id + "-n"} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span
        className={custom.invoiceNum}
        style={{ cursor: "pointer", textDecoration: "underline" }}
        onClick={() =>
          history.push(
            inv.status === "Draft"
              ? `/admin/invoices/${inv.id}/resume`
              : `/admin/invoices/${inv.id}`
          )
        }
      >
        {inv.invoiceNumber}
      </span>
      {inv.returnNumber && (
        <span style={{ fontSize: "11px", color: "#ad1457", fontWeight: 500 }}>
          {inv.returnNumber}
        </span>
      )}
    </div>,
    formatDateTime(inv.invoiceDate),
    inv.customer ? (
      <span
        key={inv.id + "-cname"}
        style={{
          color: "#1565c0",
          fontWeight: 600,
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={() => openInvDrawer(inv.customer)}
      >
        {inv.customer.name}
      </span>
    ) : (
      "—"
    ),
    <ClickableStatus
      key={inv.id + "-s"}
      status={inv.status}
      onClick={() => openDrawer(inv)}
    />,
    formatNum(inv.subtotal),
    formatNum(inv.discount),
    formatNum(inv.subtotal - inv.discount),
    formatNum(inv.paid),
  ]);

  // Active filter chips
  const activeChips = [];
  if (status) activeChips.push({ label: `Status: ${status}`, key: "status" });
  if (invoiceType)
    activeChips.push({
      label: INVOICE_TYPE_OPTIONS.find((o) => o.value === invoiceType)?.label,
      key: "invoiceType",
    });
  if (paymentMethod)
    activeChips.push({
      label: PAYMENT_METHOD_OPTIONS.find((o) => o.value === paymentMethod)
        ?.label,
      key: "paymentMethod",
    });
  if (dateFrom || dateTo) {
    const lbl =
      dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom || dateTo;
    activeChips.push({ label: `Date: ${lbl}`, key: "date" });
  }

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <ReceiptIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Invoices</h4>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => history.push("/admin/invoices/new")}
              style={{ position: "absolute", right: "16px", top: "16px" }}
            >
              Create Invoice
            </Button>
          </CardHeader>
          <CardBody>
            {error && (
              <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>
            )}

            {/* ── Filters ── */}
            <div className={custom.filterRow}>
              <div className={custom.searchBox}>
                <Search
                  style={{
                    color: "#aaa",
                    marginRight: "6px",
                    fontSize: "18px",
                    width: "1rem",
                  }}
                />
                <InputBase
                  placeholder="Invoice number..."
                  value={invoiceNumber}
                  onChange={(e) => {
                    setInvoiceNumber(e.target.value);
                    debounce({ invoiceNumber: e.target.value });
                  }}
                  style={{ fontSize: "14px", minWidth: "150px" }}
                />
              </div>

              <div className={custom.searchBox}>
                <Search
                  style={{
                    color: "#aaa",
                    marginRight: "6px",
                    fontSize: "18px",
                    width: "1rem",
                  }}
                />
                <InputBase
                  placeholder="Customer number..."
                  value={customerNumber}
                  onChange={(e) => {
                    setCustomerNumber(e.target.value);
                    debounce({ customerNumber: e.target.value });
                  }}
                  style={{ fontSize: "14px", minWidth: "150px" }}
                />
              </div>

              <div className={custom.searchBox}>
                <Search
                  style={{
                    color: "#aaa",
                    marginRight: "6px",
                    fontSize: "18px",
                    width: "1rem",
                  }}
                />
                <InputBase
                  placeholder="Customer name..."
                  value={customerName}
                  onChange={(e) => {
                    setCustomerName(e.target.value);
                    debounce({ customerName: e.target.value });
                  }}
                  style={{ fontSize: "14px", minWidth: "160px" }}
                />
              </div>
              {/* Invoice type */}
              <div className={custom.filterGroup}>
                <span className={custom.filterLabel}>Type:</span>
                <Select
                  value={invoiceType}
                  onChange={handleTypeChange}
                  disableUnderline
                  className={custom.selectBox}
                >
                  {INVOICE_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Status */}
              <div className={custom.filterGroup}>
                <span className={custom.filterLabel}>Status:</span>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  disableUnderline
                  className={custom.selectBox}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Payment method */}
              <div className={custom.filterGroup}>
                <span className={custom.filterLabel}>Payment:</span>
                <Select
                  value={paymentMethod}
                  onChange={handlePaymentChange}
                  disableUnderline
                  className={custom.selectBox}
                  style={{ minWidth: "150px" }}
                >
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Time */}
              <div className={custom.filterGroup}>
                <span className={custom.filterLabel}>Time:</span>
                <Select
                  value={timePeriod}
                  onChange={handleTimePeriodChange}
                  disableUnderline
                  className={custom.selectBox}
                  style={{ minWidth: "130px" }}
                >
                  {TIME_PRESETS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Custom date range */}
              {timePeriod === "custom" && (
                <div className={custom.filterGroup}>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={handleDateFromChange}
                    className={custom.dateInput}
                    title="From"
                  />
                  <span style={{ color: "#aaa", fontSize: "13px" }}>→</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={handleDateToChange}
                    className={custom.dateInput}
                    title="To"
                  />
                </div>
              )}
            </div>

            {/* Active filter chips */}
            {activeChips.length > 0 && (
              <div className={custom.activeFilters}>
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={() => clearFilter(chip.key)}
                    size="small"
                    style={{ fontSize: "12px" }}
                  />
                ))}
              </div>
            )}

            {/* Table */}
            <Table
              tableHead={[
                "",
                "Invoice no.",
                "Time",
                "Customer",
                "Status",
                "Sub-total",
                "Discount %",
                "Sale price",
                "Paid",
              ]}
              tableData={loading ? [] : tableData}
              customCellClasses={[
                classes.center,
                classes.right,
                classes.right,
                classes.right,
                classes.right,
              ]}
              customClassesForCells={[0, 5, 6, 7, 8]}
              customHeadCellClasses={[
                classes.center,
                classes.right,
                classes.right,
                classes.right,
                classes.right,
              ]}
              customHeadClassesForCells={[0, 5, 6, 7, 8]}
            />
            {loading && (
              <p style={{ textAlign: "center", padding: "16px 0" }}>
                Loading...
              </p>
            )}

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 0",
                flexWrap: "wrap",
                fontSize: "14px",
              }}
            >
              <span>Show</span>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                disableUnderline
                style={{
                  fontSize: "14px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "2px 8px",
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt} rows
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => goToPage(1)}
                disabled={page <= 1}
              >
                <FirstPage fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>

              <input
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={handlePageInputKey}
                onBlur={() => {
                  const pg = parseInt(pageInput, 10);
                  if (!isNaN(pg)) goToPage(pg);
                  else setPageInput(String(page));
                }}
                style={{
                  width: "48px",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px 0",
                  fontSize: "14px",
                }}
              />

              <IconButton
                size="small"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages}
              >
                <LastPage fontSize="small" />
              </IconButton>

              <span style={{ color: "#555" }}>
                {from} – {to} of {total.toLocaleString("vi-VN")} invoices
              </span>
            </div>
          </CardBody>
        </Card>
      </GridItem>

      {/* ── Customer invoice drawer ── */}
      <Drawer
        anchor="right"
        open={invDrawer.open}
        onClose={() => setInvDrawer({ open: false, customer: null })}
        classes={{ paper: custDrawer.paper }}
      >
        <div className={custDrawer.header}>
          <div>
            <div
              style={{ fontWeight: 700, fontSize: "16px", color: "#1a1a1a" }}
            >
              {invDrawer.customer?.name}
            </div>
            <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
              {custInvLoading
                ? "Loading..."
                : `${custInvTotal} invoice${custInvTotal !== 1 ? "s" : ""}`}
            </div>
          </div>
          <IconButton
            size="small"
            onClick={() => setInvDrawer({ open: false, customer: null })}
          >
            <Close fontSize="small" />
          </IconButton>
        </div>
        <Divider />
        <div className={custDrawer.body}>
          {custInvLoading ? (
            <div
              style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}
            >
              Loading...
            </div>
          ) : custInvoices.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#aaa",
                fontSize: "14px",
              }}
            >
              No invoices found
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                marginTop: "8px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    Invoice
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "6px 8px",
                      borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "6px 8px",
                      borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {custInvoices.map((inv) => {
                  const sc = STATUS_COLORS[inv.status] || {
                    bg: "#f5f5f5",
                    color: "#555",
                  };
                  const hasDebt = inv.total - inv.paid > 0;
                  return (
                    <tr
                      key={inv.id}
                      style={{ borderBottom: "1px solid #f5f5f5" }}
                    >
                      <td style={{ padding: "8px 8px" }}>
                        <span
                          style={{
                            color: "#1565c0",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => {
                            history.push(`/admin/invoices/${inv.id}`);
                            setInvDrawer({ open: false, customer: null });
                          }}
                        >
                          {inv.invoiceNumber}
                        </span>
                      </td>
                      <td style={{ padding: "8px 8px", color: "#555" }}>
                        {formatDate(inv.invoiceDate)}
                      </td>
                      <td style={{ padding: "8px 8px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: sc.bg,
                            color: sc.color,
                          }}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td style={{ padding: "8px 8px", textAlign: "right" }}>
                        <div style={{ fontWeight: 600 }}>
                          {formatNum(inv.total)}
                        </div>
                        {hasDebt && (
                          <div style={{ fontSize: "11px", color: "#f44336" }}>
                            owe {formatNum(inv.total - inv.paid)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <Divider />
        <div className={custDrawer.footer}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddShoppingCart />}
            onClick={() => {
              setInvDrawer({ open: false, customer: null });
              history.push(
                `/admin/invoices/new?customerId=${
                  invDrawer.customer?.id
                }&customerName=${encodeURIComponent(
                  invDrawer.customer?.name || ""
                )}`
              );
            }}
          >
            Create Invoice
          </Button>
        </div>
      </Drawer>

      {/* ── Status update drawer ── */}
      <Drawer
        anchor="right"
        open={!!drawerInv}
        onClose={closeDrawer}
        classes={{ paper: drawer.paper }}
      >
        {drawerInv &&
          (() => {
            const sc = STATUS_COLORS[drawerInv.status] || {
              bg: "#f5f5f5",
              color: "#555",
            };
            const nextStatuses = TRANSITIONS[drawerInv.status] || [];
            return (
              <>
                <div className={drawer.header}>
                  <span className={drawer.title}>Update status</span>
                  <IconButton size="small" onClick={closeDrawer}>
                    <Close fontSize="small" />
                  </IconButton>
                </div>

                <div className={drawer.body}>
                  {/* Invoice info */}
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#888",
                        marginBottom: "6px",
                      }}
                    >
                      Invoice
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#1565c0",
                        marginBottom: "8px",
                      }}
                    >
                      {drawerInv.invoiceNumber}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#888",
                        marginBottom: "6px",
                      }}
                    >
                      Current status
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 14px",
                        borderRadius: "14px",
                        fontSize: "13px",
                        fontWeight: 700,
                        backgroundColor: sc.bg,
                        color: sc.color,
                      }}
                    >
                      {drawerInv.status}
                    </span>
                  </div>

                  {/* New status dropdown */}
                  {nextStatuses.length > 0 ? (
                    <div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#888",
                          marginBottom: "8px",
                        }}
                      >
                        Move to
                      </div>
                      <select
                        value={drawerStatus}
                        onChange={(e) => setDrawerStatus(e.target.value)}
                        style={{
                          width: "100%",
                          fontSize: "14px",
                          padding: "8px 10px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          backgroundColor: "#fff",
                          color: "#333",
                          outline: "none",
                        }}
                      >
                        <option value="">— Select new status —</option>
                        {nextStatuses.map((s) => {
                          const c2 = STATUS_COLORS[s] || { color: "#555" };
                          return (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          );
                        })}
                      </select>
                      {drawerStatus === "Returned" && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#ad1457",
                            marginTop: "8px",
                          }}
                        >
                          You will be taken to the return creation form. Status
                          will not change until the return is submitted.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: "13px", color: "#aaa" }}>
                      No further status transitions available.
                    </p>
                  )}
                </div>

                <div className={drawer.footer}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={closeDrawer}
                    disabled={drawerSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSaveStatus}
                    disabled={!drawerStatus || drawerSaving}
                  >
                    {drawerSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </>
            );
          })()}
      </Drawer>
    </GridContainer>
  );
}
